import { useCallback, useEffect, useState } from 'react';
import type { Evento, ListaEventos } from '../../api/ops/eventos';
import { Chip } from '../../componentes/Chip';
import { Desplegable } from '../../componentes/Desplegable';
import { EstadoCargando, EstadoError, EstadoNoContratado, EstadoSinPermiso } from '../../componentes/EstadoPagina';
import { Filtros } from '../../componentes/Filtros';
import { Icono } from '../../componentes/Icono';
import { Paginacion } from '../../componentes/Paginacion';
import { Tabla } from '../../componentes/Tabla';
import { rolesDe } from '../../logica/acceso';
import { estadoDeError } from '../../logica/estados';
import { haySiguiente, usePaginacionCursor } from '../../logica/paginacion';
import { useCabecera } from '../../marco/cabecera';
import { usePermisos } from '../../permisos/permisos';
import type { PropsPantalla } from '../tipos';
import { Ficha } from './Ficha';
import { NuevoEvento } from './NuevoEvento';
import { BarraAforo } from './piezas';
import { AYUDA, chipFechaCambiada, opsDe, type OpsEventos, consultaDeFiltro, enFiltro, ESTADO_EVENTO, FILTROS, filtroDe, lineaAforo, lineaModo, lineaVentas, permisosEventos, rangoEn } from './reglas';
import '../../componentes/apartado.css';
import '../avisos/avisos.css';
import './eventos.css';

/**
 * «Eventos» (entrada `entradas`, ruta `/entradas`; 10.1 a 10.5). Lista con los filtros Próximos · Borradores · Publicados ·
 * Finalizados · Cancelados (sin cifras, G121) y, con varias sedes, la sede (`listLocations`); «Anterior / Siguiente» por
 * cursor. La ficha (10.2 a 10.5) se abre con `?evento=<id>`; el alta, con «Nuevo evento» en la cabecera.
 * `?filtro=` y `?sede=` guardan la vista en la dirección. Quien no tiene `entradas.eventos.gestionar` solo ve.
 */
export interface PropsEventos extends PropsPantalla {
    api: OpsEventos;
}

export function Eventos(props: PropsPantalla) {
    const { negocio, busqueda, ruta, navegar, movil, soloLectura, membresia, nombreNegocio } = props;
    const api = opsDe(props.ops);
    const { puede } = usePermisos();
    const permisos = permisosEventos(puede);
    const crea = permisos.gestiona && !soloLectura;
    const filtro = filtroDe(busqueda.get('filtro'));
    const sede = busqueda.get('sede');
    const elegido = busqueda.get('evento');
    const [leida, setLeida] = useState<{ clave: string; lista: ListaEventos } | null>(null);
    const [sedes, setSedes] = useState<{ id: string; nombre: string }[]>([]);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const [nuevo, setNuevo] = useState(false);
    const pag = usePaginacionCursor();
    const { reiniciar } = pag;
    const recargarLista = useCallback(() => { setIntento((i) => i + 1); }, []);
    const base = ruta.split('?')[0] ?? ruta;

    const irA = (cambios: Record<string, string | null>) => {
        const q = new URLSearchParams(busqueda);
        for (const [k, v] of Object.entries(cambios)) {
            if (v === null || v === '') {
                q.delete(k);
            } else {
                q.set(k, v);
            }
        }
        const texto = q.toString();
        navegar(texto === '' ? base : `${base}?${texto}`, true);
    };

    useCabecera({
        ayuda: AYUDA.lista,
        acciones: crea ? [{ clave: 'nuevo', texto: 'Nuevo evento', icono: 'plus', onClick: () => { setNuevo(true); } }] : [],
    });

    useEffect(() => {
        let vivo = true;
        api.listLocations(negocio, { limit: 100 })
            .then((r) => { if (vivo) { setSedes(r.data.filter((s) => s.status === 'active').map((s) => ({ id: s.id, nombre: s.name }))); } })
            .catch(() => undefined);
        return () => { vivo = false; };
    }, [api, negocio]);

    useEffect(() => { reiniciar(); }, [filtro, sede, reiniciar]);

    const clave = JSON.stringify([filtro, sede, pag.cursor, intento]);
    const lista = leida?.clave === clave ? leida.lista : null;
    useEffect(() => {
        let vivo = true;
        api.listEvents(negocio, { ...consultaDeFiltro(filtro, sede), cursor: pag.cursor ?? undefined, limit: 25 })
            .then((r) => { if (vivo) { setLeida({ clave: JSON.stringify([filtro, sede, pag.cursor, intento]), lista: r }); setError(null); } })
            .catch((e: unknown) => { if (vivo) { setError(e); } });
        return () => { vivo = false; };
    }, [api, negocio, filtro, sede, pag.cursor, intento]);

    if (error !== null) {
        const est = estadoDeError(error);
        if (est.tipo === 'sin-permiso') {
            return <EstadoSinPermiso seccion="los eventos" rol={membresia === undefined ? '' : rolesDe(membresia)} negocio={nombreNegocio} onInicio={() => { navegar('/'); }} movil={movil} />;
        }
        if (est.tipo === 'no-contratado') {
            return (
                <EstadoNoContratado
                    bloque="Entradas y eventos"
                    texto="Vende entradas de tus eventos por la web, el widget, el asistente y la taquilla, sin cuota: el comprador paga 1 € + 1,5 % por entrada."
                    incluye={['Un aforo único para entradas, listas y taquilla: nunca vendes de más', 'Taquilla en la puerta, también sin conexión', 'Si cambia la fecha o se cancela, avisas y devuelves en un momento']}
                    propietario={membresia?.is_owner === true}
                    onAnadir={membresia?.is_owner === true ? () => { navegar('/plan?block=entradas_eventos'); } : null}
                    onInicio={() => { navegar('/'); }}
                    movil={movil}
                />
            );
        }
        return <EstadoError seccion="los eventos" codigoSoporte={est.codigoSoporte} onReintentar={recargarLista} movil={movil} />;
    }

    const eventos = (lista?.data ?? []).filter((e) => enFiltro(filtro, e));
    const sedeDe = (id: string | undefined) => sedes.find((s) => s.id === id)?.nombre ?? '';
    const nota = soloLectura && permisos.gestiona
        ? 'El negocio está en solo lectura: puedes consultar y cancelar un evento (se devuelve el dinero), pero no crear, editar ni publicar.'
        : !permisos.gestiona
          ? 'Con tu rol ves los eventos y su aforo. Crearlos, publicarlos o cancelarlos lo hace el propietario o un administrador.'
          : null;
    const vaciaDelTodo = filtro === 'proximos' && sede === null && !pag.hayAnterior;
    const nombreFiltro = FILTROS.find((f) => f.valor === filtro)?.vacia ?? '';

    return (
        <div className="rv-en-pantalla rv-ev">
            <div className="rv-ev-filtros">
                <Filtros
                    etiqueta="Eventos"
                    items={FILTROS.map((f) => ({ etiqueta: f.etiqueta }))}
                    valor={FILTROS.findIndex((f) => f.valor === filtro)}
                    onCambio={(i) => { irA({ filtro: (FILTROS[i]?.valor ?? 'proximos') === 'proximos' ? null : (FILTROS[i]?.valor ?? null) }); }}
                />
                {sedes.length > 1 && (
                    <div className="rv-ev-sede">
                        <Desplegable etiqueta="Sede" valor={sede ?? ''} opciones={[{ valor: '', texto: 'Todas las sedes' }, ...sedes.map((s) => ({ valor: s.id, texto: s.nombre }))]} onChange={(v) => { irA({ sede: v }); }} />
                    </div>
                )}
            </div>
            {nota !== null && (
                <p className="rv-ca-nota">
                    <Icono nombre={soloLectura ? 'lock' : 'eye'} />
                    <span>{nota}</span>
                </p>
            )}
            {lista === null ? (
                <EstadoCargando seccion="los eventos" />
            ) : eventos.length === 0 ? (
                <div className="rv-ca-vacia">
                    <span className="rv-ca-vacia__baldosa">
                        <Icono nombre={vaciaDelTodo ? 'ticket' : 'inbox'} />
                    </span>
                    <strong>{vaciaDelTodo ? 'Todavía no hay eventos' : `No hay eventos ${nombreFiltro}${sede === null ? '' : ` en ${sedeDe(sede)}`}`}</strong>
                    <span>
                        {vaciaDelTodo
                            ? crea
                                ? 'Crea tu primer evento: fechas, aforo y entradas. Se queda en borrador hasta que lo publiques.'
                                : 'Cuando el propietario o un administrador cree un evento, lo verás aquí.'
                            : 'Prueba con otro filtro.'}
                    </span>
                    {vaciaDelTodo && crea && (
                        <button type="button" className="rv-b rv-b-pri" onClick={() => { setNuevo(true); }}>
                            <Icono nombre="plus" />
                            <span>Crear el primer evento</span>
                        </button>
                    )}
                    {!vaciaDelTodo && (
                        <button type="button" className="rv-b rv-b-sec" onClick={() => { irA({ filtro: null, sede: null }); }}>
                            <Icono nombre="x" />
                            <span>Ver todos los próximos</span>
                        </button>
                    )}
                </div>
            ) : (
                <Tabla
                    etiqueta="Eventos del negocio"
                    movil={movil}
                    columnas={[
                        { clave: 'evento', titulo: 'Evento', ancho: 'minmax(240px, 2.2fr)' },
                        { clave: 'estado', titulo: 'Estado', ancho: 'minmax(130px, 0.9fr)' },
                        { clave: 'ventas', titulo: 'Ventas', ancho: 'minmax(120px, 0.8fr)' },
                        { clave: 'aforo', titulo: 'Aforo', ancho: 'minmax(200px, 1.4fr)' },
                    ]}
                    filas={eventos.map((e) => filaDe(e, sedeDe(e.location_id), () => { irA({ evento: e.id }); }))}
                />
            )}
            {lista !== null && (pag.hayAnterior || haySiguiente(lista.next_cursor)) && (
                <Paginacion hayAnterior={pag.hayAnterior} haySiguiente={haySiguiente(lista.next_cursor)} onAnterior={pag.anterior} onSiguiente={() => { pag.siguiente(lista.next_cursor); }} movil={movil} />
            )}
            {nuevo && (
                <NuevoEvento
                    {...props}
                    api={api}
                    sedes={sedes}
                    onCerrar={(creado) => {
                        setNuevo(false);
                        if (creado !== undefined) {
                            recargarLista();
                            irA({ evento: creado.id });
                        }
                    }}
                />
            )}
            {elegido !== null && !nuevo && (
                <Ficha
                    key={elegido}
                    {...props}
                    api={api}
                    id={elegido}
                    sedes={sedes}
                    permisos={permisos}
                    onCambiado={recargarLista}
                    onCerrar={() => { irA({ evento: null }); }}
                />
            )}
        </div>
    );
}

function filaDe(e: Evento, sede: string, abrir: () => void) {
    const [estado, tono] = ESTADO_EVENTO[e.status];
    const ventas = lineaVentas(e);
    const aforo = lineaAforo(e);
    const fecha = chipFechaCambiada(e);
    const cuando = [rangoEn(e.starts_at, e.ends_at, e.timezone), sede].filter((x) => x !== '').join(' · ');
    const evento = (
        <span className="rv-ev-celda-evento">
            <span className="rv-ev-cartel" aria-hidden="true">{e.image_url != null ? <img src={e.image_url} alt="" /> : (e.name ?? '?').slice(0, 1).toLocaleUpperCase('es')}</span>
            <span className="rv-en-celda">
                <strong>{e.name}</strong>
                <small>{cuando}</small>
                {fecha !== '' && (
                    <small className="rv-ev-fecha-cambiada">
                        <Icono nombre="calendar-clock" />
                        {fecha}
                    </small>
                )}
            </span>
        </span>
    );
    return {
        clave: e.id,
        etiqueta: e.name ?? e.id,
        onAbrir: abrir,
        tarjeta: (
            <span className="rv-en-celda">
                {evento}
                <small>{`${estado} · ${ventas.linea} · ${aforo.cifra}${aforo.libres === '' ? '' : ` · ${aforo.libres}`}`}</small>
            </span>
        ),
        celdas: {
            evento,
            estado: (
                <span className="rv-en-celda">
                    {e.status === 'published' ? <strong>{estado}</strong> : <Chip estado={tono}>{estado}</Chip>}
                    <small>{lineaModo(e)}</small>
                </span>
            ),
            ventas: (
                <span className="rv-en-celda">
                    <strong>{ventas.linea}</strong>
                    {ventas.sub !== '' && <small>{ventas.sub}</small>}
                </span>
            ),
            aforo: (
                <span className="rv-ev-celda-aforo">
                    <BarraAforo aforo={e.capacity_detail} fina />
                    <span className="rv-ev-celda-aforo__linea">
                        <strong>{aforo.cifra}</strong>
                        <span className={aforo.completo ? 'rv-ev-completo' : ''}>{aforo.libres}</span>
                    </span>
                </span>
            ),
        },
    };
}
